import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { CheckCircle } from 'lucide-react';

function Profile() {
  const { user, updateProfile } = useAuth();

  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [contact, setContact] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ password: '', confirmPassword: '', email: '' });

  useEffect(() => {
    if (user) {
      setNickname(user.nickname || '');
      setEmail(user.email || '');
      setPixKey(user.pixKey || '');
      setContact(user.contact || '');
    }
  }, [user]);

  const validateField = (field, value, extraData = {}) => {
    let msg = '';
    if (field === 'password' && value && value.length < 6) {
      msg = 'Mínimo 6 caracteres';
    }
    if (field === 'confirmPassword') {
      const pw = extraData.password !== undefined ? extraData.password : password;
      if (value && value !== pw) msg = 'As senhas não coincidem';
    }
    if (field === 'email' && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      msg = 'Formato de email inválido';
    }
    setFieldErrors(prev => ({ ...prev, [field]: msg }));
    return msg;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    let hasError = false;
    if (password && password.length < 6) {
      validateField('password', password);
      hasError = true;
    }
    if (password && password !== confirmPassword) {
      validateField('confirmPassword', confirmPassword, { password });
      hasError = true;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      validateField('email', email);
      hasError = true;
    }
    if (hasError) return;

    setIsSubmitting(true);

    const profileData = {
      nickname: nickname.trim(),
      email: email.trim(),
      pixKey: pixKey.trim(),
      contact: contact.trim(),
    };

    if (password) {
      profileData.password = password;
    }

    const result = await updateProfile(profileData);

    setIsSubmitting(false);

    if (result.success) {
      setSuccessMessage('Perfil atualizado com sucesso!');
      setPassword('');
      setConfirmPassword('');
      setFieldErrors({ password: '', confirmPassword: '', email: '' });
    } else {
      setError(result.error);
    }
  };

  if (!user) {
    return <div className="container">Faça login para editar seu perfil.</div>;
  }

  return (
    <div className="container max-w-md">
      <h1 className="text-2xl font-bold mb-6 text-center text-primary">Meu Perfil</h1>

      {error && (
        <div className="alert alert-error mb-4" role="alert">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded flex items-center gap-2" role="status">
          <CheckCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card" noValidate>
        <div className="form-control mb-4">
          <label className="form-label font-semibold">Nome Completo</label>
          <input
            type="text"
            className="form-input bg-gray-100"
            value={user.name}
            disabled
          />
          <p className="text-sm text-gray-500 mt-1">O nome completo não pode ser alterado.</p>
        </div>

        <div className="form-control mb-4">
          <label className="form-label font-semibold" htmlFor="profile-nickname">Nickname</label>
          <input
            id="profile-nickname"
            type="text"
            className="form-input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Seu nickname"
          />
        </div>

        <div className="form-control mb-4">
          <label className="form-label font-semibold" htmlFor="profile-email">Email</label>
          <input
            id="profile-email"
            type="email"
            className={`form-input${fieldErrors.email ? ' border-red-500' : ''}`}
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) validateField('email', e.target.value); }}
            onBlur={() => validateField('email', email)}
            placeholder="seu.email@exemplo.com"
            aria-invalid={fieldErrors.email ? 'true' : undefined}
            aria-describedby={fieldErrors.email ? 'profile-email-error' : undefined}
          />
          {fieldErrors.email && <p id="profile-email-error" className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>}
        </div>

        <div className="form-control mb-4">
          <label className="form-label font-semibold" htmlFor="profile-pixkey">Chave PIX</label>
          <input
            id="profile-pixkey"
            type="text"
            className="form-input"
            value={pixKey}
            onChange={(e) => setPixKey(e.target.value)}
            placeholder="Sua chave PIX para recebimento"
          />
        </div>

        <div className="form-control mb-4">
          <label className="form-label font-semibold" htmlFor="profile-contact">Contato/Telefone</label>
          <input
            id="profile-contact"
            type="text"
            className="form-input"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Seu telefone com DDD"
          />
        </div>

        <hr className="my-6 border-gray-200" />

        <h2 className="text-lg font-semibold mb-4 text-gray-700">Alterar Senha (Opcional)</h2>

        <div className="form-control mb-4 relative">
          <label className="form-label font-semibold" htmlFor="profile-password">Nova Senha</label>
          <div className="relative">
            <input
              id="profile-password"
              type={showPassword ? "text" : "password"}
              className={`form-input w-full pr-10${fieldErrors.password ? ' border-red-500' : ''}`}
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) validateField('password', e.target.value); if (fieldErrors.confirmPassword) validateField('confirmPassword', confirmPassword, { password: e.target.value }); }}
              onBlur={() => { if (password) validateField('password', password); }}
              placeholder="Deixe em branco para manter a atual"
              aria-invalid={fieldErrors.password ? 'true' : undefined}
              aria-describedby={fieldErrors.password ? 'profile-password-error' : 'profile-password-help'}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-primary-dark focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          {!fieldErrors.password && <p id="profile-password-help" className="text-gray-500 text-xs mt-1">Mínimo 6 caracteres</p>}
          {fieldErrors.password && <p id="profile-password-error" className="text-red-600 text-sm mt-1">{fieldErrors.password}</p>}
        </div>

        <div className="form-control mb-6 relative">
          <label className="form-label font-semibold" htmlFor="profile-confirm-password">Confirmar Nova Senha</label>
          <div className="relative">
            <input
              id="profile-confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              className={`form-input w-full pr-10${fieldErrors.confirmPassword ? ' border-red-500' : ''}`}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); if (fieldErrors.confirmPassword) validateField('confirmPassword', e.target.value, { password }); }}
              onBlur={() => { if (confirmPassword) validateField('confirmPassword', confirmPassword, { password }); }}
              placeholder="Confirme a nova senha"
              aria-invalid={fieldErrors.confirmPassword ? 'true' : undefined}
              aria-describedby={fieldErrors.confirmPassword ? 'profile-confirm-password-error' : undefined}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-primary-dark focus:outline-none"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showConfirmPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          {fieldErrors.confirmPassword && <p id="profile-confirm-password-error" className="text-red-600 text-sm mt-1">{fieldErrors.confirmPassword}</p>}
        </div>

        <button
          type="submit"
          className="btn btn-primary w-full focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </form>
    </div>
  );
}

export default Profile;
